// Rich per-suburb SEO content. Each suburb has genuinely unique copy —
// different opening angle, different local details, different job mix.
// Estates listed here are those confirmed by the business. Nearby estates
// (in adjacent suburbs) are surfaced on the closest listed page so they
// also get indexed without needing a dedicated suburb entry.

export const SUBURB_CONTENT = {
  "epping-vic": {
    name: "Epping",
    postcode: "3076",
    metaTitle: "Site Clean-Up Epping VIC 3076 | Vacant Lot & Builder Waste",
    metaDesc: "Free site clean-up quotes in Epping VIC 3076. Vacant lot clean-ups, builder waste removal and end-of-build tidy-ups — including Hargrove estate.",
    heading: "Site clean-up in Epping",
    intro: "Epping sits at the northern end of Melbourne's growth corridor — an established suburb with the Cooper Street industrial precinct, Epping Central and Northern Hospital anchoring it, transitioning into the newer subdivided lots that spill toward Wollert. Site clean-up work here is a mix of old and new: rental turnovers, subdivided block clearances and pre-build lot tidy-ups on the Hargrove side of the suburb.",
    estates: [
      { name: "Hargrove", note: "New-release lots on the Epping / Wollert boundary" },
    ],
    nearbyEstates: [
      { name: "Marran Run", suburb: "Thomastown", note: "Also serviced from our Epping enquiries" },
    ],
    jobs: [
      "Vacant subdivided lot clean-up before builder handover",
      "Skip bin and end-of-build waste on new Hargrove homes",
      "Cooper Street commercial and industrial precinct site tidy-ups",
      "Rental property clean-outs on established Epping streets",
      "Green waste and vegetation removal on older larger lots",
    ],
    localNotes: "Truck access into Epping is generally straightforward via Cooper Street and High Street. Contractors matched to your job typically handle tip runs through the Cooper Street transfer stations. Older Epping blocks often have narrower rear-access laneways — a photo of the driveway helps us match the right truck size.",
    keywords: ["site clean-up Epping", "vacant lot clean-up Epping", "builder waste removal Epping", "Hargrove estate clean-up"],
  },

  "wollert": {
    name: "Wollert",
    postcode: "3750",
    metaTitle: "Site Clean-Up Wollert VIC 3750 | Aurora, Mason Quarter, Lyndarum North",
    metaDesc: "Free clean-up quotes in Wollert — Stockland Aurora, Mason Quarter, Zoe Fields, Wollert Rise and Lyndarum North estates. Vacant lot clean-up and builder waste removal.",
    heading: "Site clean-up in Wollert",
    intro: "Wollert has been one of Melbourne's fastest-growing suburbs for several years, with dozens of masterplanned estates in active development at any given time. If you've bought or are building on a lot in Stockland Aurora, Mason Quarter, Zoe Fields, Wollert Rise or Lyndarum North, this is a suburb where builders mobilise new jobs almost weekly — and getting the block cleared and ready on time genuinely matters.",
    estates: [
      { name: "Stockland Aurora", note: "One of the largest masterplanned communities in the north" },
      { name: "Mason Quarter", note: "New-release lots with active construction" },
      { name: "Zoe Fields", note: "Family estate with ongoing releases" },
      { name: "Wollert Rise", note: "Growing pocket in the suburb's north" },
      { name: "Lyndarum North", note: "Established Villawood estate" },
    ],
    jobs: [
      "Vacant lot clean-up before slab-down in Aurora, Mason Quarter and Zoe Fields",
      "Builder waste removal during frame, lock-up and fixing stages",
      "Post-handover site tidy-ups on newly finished Lyndarum North and Wollert Rise homes",
      "Removal of dumped rubbish from unbuilt display lots",
      "Skip bin coordination for ongoing builder programs",
    ],
    localNotes: "Wollert's fresh estate roads make truck access simple, but scheduling around neighbouring active builds and estate management rules matters. Contractors we match generally coordinate directly with your site supervisor or estate manager on bin placement and access windows.",
    keywords: ["site clean-up Wollert", "vacant lot clean-up Wollert", "builder waste removal Wollert", "Aurora estate clean-up", "Mason Quarter clean-up", "Lyndarum North builder waste"],
  },

  "craigieburn": {
    name: "Craigieburn",
    postcode: "3064",
    metaTitle: "Site Clean-Up Craigieburn VIC 3064 | Montview & Aston by Peet",
    metaDesc: "Free site clean-up quotes in Craigieburn 3064 — Montview Estate and Aston by Peet. Vacant lot clean-up, builder waste removal and infill development tidy-ups.",
    heading: "Site clean-up in Craigieburn",
    intro: "Craigieburn is the mature end of Melbourne's northern growth corridor. It has its own shopping precinct at Craigieburn Central, its own train line, and a mix of legacy homes alongside continued estate expansion. Site clean-up demand here leans toward the newer estate releases and small infill subdivisions rather than raw greenfield work.",
    estates: [
      { name: "Montview Estate", note: "New-release lots on Craigieburn's north side" },
      { name: "Aston by Peet", note: "Established Peet estate with ongoing releases" },
    ],
    nearbyEstates: [
      { name: "MB Greenvale", suburb: "Greenvale", note: "Serviced from our Craigieburn enquiries" },
      { name: "Dunhelen", suburb: "Greenvale", note: "Estate near the Craigieburn boundary" },
      { name: "Viridian", suburb: "Greenvale", note: "Newer release adjacent to Craigieburn" },
    ],
    jobs: [
      "Vacant lot clean-up on Montview Estate and Aston by Peet releases",
      "Builder waste removal on infill developments and townhouse builds",
      "Renovation and owner-occupier waste on established Craigieburn streets",
      "Small-scale land clearing prep on subdivided lots",
      "End-of-build handover tidy-ups",
    ],
    localNotes: "Craigieburn's spread means access and travel time vary between the older township and the newer northern estates. Sharing your street address and photos up front helps us match a contractor already working in your pocket.",
    keywords: ["site clean-up Craigieburn", "vacant lot clean-up Craigieburn", "builder waste removal Craigieburn", "Montview Estate clean-up", "Aston by Peet builder waste"],
  },

  "mickleham": {
    name: "Mickleham",
    postcode: "3064",
    metaTitle: "Site Clean-Up Mickleham VIC 3064 | Highlands, Merrifield, Botanical",
    metaDesc: "Free site clean-up quotes in Mickleham 3064 — Stockland Highlands, Merrifield, Botanical, Hollington Quarter, Novella. Vacant lot clean-up and builder waste removal.",
    heading: "Site clean-up in Mickleham",
    intro: "Mickleham has transformed over the past decade from rural fringe into one of the north's most active masterplanned communities. Stockland Highlands, Merrifield, Botanical, Hollington Quarter and Novella have reshaped the suburb's identity, and site clean-up enquiries here are dominated by new-build and pre-build work rather than legacy residential.",
    estates: [
      { name: "Stockland Highlands", note: "Long-standing masterplanned community" },
      { name: "Hollington Quarter", note: "Newer townhome and lot release" },
      { name: "Novella", note: "Family estate with ongoing releases" },
      { name: "Botanical", note: "Landscape-focused masterplanned community" },
      { name: "Merrifield", note: "One of the largest active estates in the north" },
    ],
    nearbyEstates: [
      { name: "Stockland Cloverton", suburb: "Kalkallo", note: "Adjacent to Mickleham, serviced under the same enquiries" },
    ],
    jobs: [
      "Pre-build lot clearance on new Highlands, Merrifield and Botanical releases",
      "Builder waste and packaging removal during construction",
      "End-of-build handover clean-ups for photography and settlement",
      "Green waste and light vegetation on freshly released blocks",
      "Removal of contractor rubbish and offcuts between build stages",
    ],
    localNotes: "Estate covenants and bin placement rules vary across Mickleham's masterplanned communities. Contractors matched to your job usually confirm access windows and skip locations with the estate manager before arriving.",
    keywords: ["site clean-up Mickleham", "vacant lot clean-up Mickleham", "builder waste removal Mickleham", "Merrifield clean-up", "Stockland Highlands clean-up", "Botanical estate builder waste"],
  },

  "donnybrook": {
    name: "Donnybrook",
    postcode: "3064",
    metaTitle: "Site Clean-Up Donnybrook VIC 3064 | Katalia, Olivine, Peppercorn Hill",
    metaDesc: "Free clean-up quotes in Donnybrook — Stockland Katalia, Olivine by Mirvac, Peppercorn Hill, Heartford, Matilda, Balmain, Acclaim. Vacant lot & builder waste specialists.",
    heading: "Site clean-up in Donnybrook",
    intro: "Donnybrook is Melbourne's newest large-scale growth suburb — arguably the most active greenfield postcode in the north right now. Between Stockland Katalia, Olivine by Mirvac, Peppercorn Hill, Heartford, Matilda, Balmain and Acclaim Donnybrook, tens of thousands of new lots are moving through pre-build, build and handover phases every month.",
    estates: [
      { name: "Stockland Katalia", note: "Flagship Stockland community with major ongoing releases" },
      { name: "Olivine by Mirvac", note: "Established Mirvac masterplan" },
      { name: "Peppercorn Hill", note: "Northern Donnybrook release" },
      { name: "Heartford", note: "Family estate with active builds" },
      { name: "Matilda", note: "Newer release community" },
      { name: "Balmain", note: "Growing estate on the Donnybrook / Kalkallo edge" },
      { name: "Acclaim Donnybrook", note: "Boutique masterplanned release" },
    ],
    jobs: [
      "Vacant lot clean-up before builder mobilisation in Katalia and Olivine",
      "Ongoing builder waste removal during frame and lock-up",
      "Post-handover site tidy for photography, inspection and settlement",
      "Site rubbish and packaging clearance on multi-lot builder programs",
      "Green waste and light vegetation on freshly released blocks",
    ],
    localNotes: "Donnybrook's rapid growth means we see high enquiry volumes from this postcode. Send us your builder's confirmed start date and a couple of photos as early as you can — that gives us the best chance to quote and book a contractor into your timeline, especially during peak construction months.",
    keywords: ["site clean-up Donnybrook", "vacant lot clean-up Donnybrook", "builder waste removal Donnybrook", "Katalia clean-up", "Olivine clean-up", "Peppercorn Hill builder waste", "Acclaim Donnybrook clean-up"],
  },

  "mernda": {
    name: "Mernda",
    postcode: "3754",
    metaTitle: "Site Clean-Up Mernda VIC 3754 | Aspect Estate & Vacant Lots",
    metaDesc: "Free site clean-up quotes in Mernda 3754 — Aspect estate, subdivided lots and end-of-build handovers. Vacant lot clean-up, builder waste and green waste removal.",
    heading: "Site clean-up in Mernda",
    intro: "Mernda is an established family suburb at the top of the north-eastern rail line, blending older township pockets around Plenty Road with newer estates like Aspect. Site clean-up work in Mernda spans residential renovations, subdivided block clearances and post-handover tidy-ups — often on larger backyards than you'd find in the newer northern growth suburbs.",
    estates: [
      { name: "Aspect", note: "Established family estate with continued releases" },
    ],
    nearbyEstates: [
      { name: "St Helena Place", suburb: "St Helena", note: "Serviced under our north-east enquiries" },
    ],
    jobs: [
      "Vacant lot and subdivided block clean-up in Aspect",
      "Renovation and owner-occupier waste on established Mernda streets",
      "Green waste and vegetation removal on larger older blocks",
      "End-of-build handover tidy-ups on new dwellings",
      "Rental turnover clean-outs on established properties",
    ],
    localNotes: "Mernda's mix of older larger lots and newer estate blocks means job size varies considerably. A single wide photo from the road usually tells us most of what we need to know for a first-pass quote.",
    keywords: ["site clean-up Mernda", "vacant lot clean-up Mernda", "builder waste removal Mernda", "Aspect estate clean-up"],
  },

  "south-morang": {
    name: "South Morang",
    postcode: "3752",
    metaTitle: "Site Clean-Up South Morang VIC 3752 | The Ridge at Riverhills",
    metaDesc: "Free site clean-up quotes in South Morang 3752 — The Ridge at Riverhills. Vacant lot clean-up, renovation waste and end-of-build tidy-ups.",
    heading: "Site clean-up in South Morang",
    intro: "South Morang is a mature outer-northern suburb with Plenty Gorge Park on its doorstep, the Westfield Plenty Valley shopping precinct at its centre and a mix of family homes and newer infill developments like The Ridge at Riverhills. Clean-up enquiries here tend to come from renovations, subdivided older-lot developments and townhouse infill rather than raw greenfield work.",
    estates: [
      { name: "The Ridge at Riverhills", note: "Infill estate on South Morang's edge" },
    ],
    jobs: [
      "Vacant lot clean-up at The Ridge at Riverhills",
      "Renovation and rubble removal on established South Morang properties",
      "Green waste from larger established gardens and Plenty Gorge-adjacent lots",
      "End-of-build tidy-ups on infill townhouses",
      "Small demolition and slab prep tidy on subdivided lots",
    ],
    localNotes: "Access via Plenty Road and McDonalds Road is usually straightforward. Blocks bordering Plenty Gorge sometimes need smaller-machinery access — mention any slope or narrow driveway in your enquiry.",
    keywords: ["site clean-up South Morang", "vacant lot clean-up South Morang", "builder waste removal South Morang", "Ridge at Riverhills clean-up"],
  },

  "tarneit": {
    name: "Tarneit",
    postcode: "3029",
    metaTitle: "Site Clean-Up Tarneit VIC 3029 | Vacant Lot & Builder Waste",
    metaDesc: "Free site clean-up quotes in Tarneit 3029. Vacant lot clean-up, builder waste removal and end-of-build tidy-ups across Melbourne's western growth corridor.",
    heading: "Site clean-up in Tarneit",
    intro: "Tarneit is Melbourne's western growth-corridor powerhouse — one of the highest-volume new-build postcodes in Victoria. Vacant lot clean-ups, builder waste removal and end-of-build tidy-ups make up the bulk of enquiries we receive from this side of town, driven by dozens of active family estates.",
    estates: [],
    jobs: [
      "Vacant lot clean-up on new-release blocks across Tarneit's northern and western estates",
      "Builder waste removal during frame and lock-up stages",
      "End-of-build tidy-ups on completed homes ready for handover",
      "Removal of dumped rubbish from vacant residential lots",
      "Skip bin coordination on ongoing builder programs",
    ],
    localNotes: "Tarneit's newer estate roads make truck access easy, but scheduling around actively building neighbours matters — especially in tight cul-de-sacs. Sending us your street address and preferred day helps us match a contractor already working in your pocket that week.",
    keywords: ["site clean-up Tarneit", "vacant lot clean-up Tarneit", "builder waste removal Tarneit", "construction rubbish removal Tarneit"],
  },

  "clyde": {
    name: "Clyde",
    postcode: "3978",
    metaTitle: "Site Clean-Up Clyde VIC 3978 | Vacant Lot & Builder Waste Removal",
    metaDesc: "Free site clean-up quotes in Clyde and Clyde North 3978. Vacant lot clean-up, builder waste removal and pre-build site tidy-ups in Melbourne's south-east growth corridor.",
    heading: "Site clean-up in Clyde",
    intro: "Clyde and Clyde North sit at the heart of Melbourne's south-east growth corridor. New masterplanned estates, larger family lots and continued expansion drive most of the clean-up enquiries we receive from the area — mostly pre-build vacant lot work and end-of-build tidy-ups on newly finished homes.",
    estates: [],
    jobs: [
      "Pre-build vacant lot clean-up on newly released Clyde and Clyde North blocks",
      "Builder waste removal during frame and lock-up",
      "Post-handover tidy-ups on new homes ready for settlement",
      "Small-scale land clearing prep on family-sized lots",
      "Skip bin coordination on active builds",
    ],
    localNotes: "Clyde and Clyde North are geographically well away from the northern growth corridor, so contractors matched to jobs here are typically south-east based. That means you're getting a crew familiar with local tip locations and estate rules, not one making a long haul.",
    keywords: ["site clean-up Clyde", "vacant lot clean-up Clyde", "builder waste removal Clyde", "Clyde North construction rubbish removal"],
  },

  // Suburbs kept from the original list but without dedicated deep content —
  // these fall back to a generic template in the ServiceAreas page.
  "werribee": {
    name: "Werribee",
    postcode: "3030",
    metaTitle: "Site Clean-Up Werribee VIC 3030 | Vacant Lot & Builder Waste",
    metaDesc: "Free site clean-up quotes in Werribee 3030 — vacant lot clean-up, builder waste removal and end-of-build tidy-ups in Melbourne's west.",
    heading: "Site clean-up in Werribee",
    intro: "Werribee blends established residential streets, industrial precincts and continued new-build activity toward its western fringe. Clean-up enquiries here cover a broad mix — from renovation waste and subdivided infill through to pre-build vacant lot work.",
    estates: [],
    jobs: [
      "Vacant lot clean-up in Werribee's newer estate pockets",
      "Builder waste removal on infill townhouse developments",
      "Renovation, rubble and green waste on established properties",
      "End-of-build tidy-ups on completed homes",
    ],
    localNotes: "Werribee's mix of old and new means job size varies a lot — sending a photo or two makes for a much more accurate first quote.",
    keywords: ["site clean-up Werribee", "vacant lot clean-up Werribee", "builder waste removal Werribee"],
  },

  "melbourne-metro": {
    name: "Melbourne Metro",
    postcode: "",
    metaTitle: "Site Clean-Up Melbourne Metro | Vacant Lot & Builder Waste",
    metaDesc: "Free site clean-up quotes across Melbourne metro. Vacant lot clean-up, builder waste removal, pre-build clearing and end-of-build tidy-ups.",
    heading: "Site clean-up across Melbourne Metro",
    intro: "We take enquiries from right across Melbourne metro. Coverage is strongest in the northern growth corridor (Wollert, Craigieburn, Mickleham, Donnybrook, Epping, Mernda, South Morang), the western corridor (Tarneit, Werribee) and the south-east (Clyde, Clyde North), and expands from there via matched local contractors.",
    estates: [],
    jobs: [
      "Vacant lot clean-up before new builds",
      "Builder waste and packaging removal during construction",
      "End-of-build handover tidy-ups",
      "Renovation and infill development clean-outs",
    ],
    localNotes: "Metro-wide coverage means response time depends on local contractor availability in your suburb — always share your street address so we can match the closest crew.",
    keywords: ["site clean-up Melbourne", "vacant lot clean-up Melbourne", "builder waste removal Melbourne"],
  },

  "regional-victoria": {
    name: "Regional Victoria",
    postcode: "",
    metaTitle: "Site Clean-Up Regional Victoria | Rural & Regional Enquiries",
    metaDesc: "Regional Victoria site clean-up enquiries — vacant lot clean-ups, builder waste and rural site tidy-ups. Coverage subject to local contractor availability.",
    heading: "Site clean-up across Regional Victoria",
    intro: "We accept enquiries from regional Victoria and match them where a suitable local contractor is available. Common jobs include vacant lot clean-ups on rural residential blocks, small builder waste programs and end-of-build tidy-ups outside the metro area.",
    estates: [],
    jobs: [
      "Vacant lot clean-up on rural residential and lifestyle blocks",
      "Small builder waste and offcut removal programs",
      "Land clearing prep and light vegetation removal",
      "End-of-build tidy-ups on regional new homes",
    ],
    localNotes: "Regional coverage varies significantly by contractor availability. Please share your town and postcode so we can be upfront about whether we can service your job.",
    keywords: ["site clean-up regional Victoria", "rural block clean-up VIC", "builder waste removal regional Victoria"],
  },
};

// Suggest 3 related suburbs per page (for internal linking)
export const NEARBY = {
  "epping-vic": ["wollert", "mernda", "south-morang"],
  "wollert": ["epping-vic", "mickleham", "donnybrook"],
  "craigieburn": ["mickleham", "donnybrook", "wollert"],
  "mickleham": ["craigieburn", "donnybrook", "wollert"],
  "donnybrook": ["mickleham", "wollert", "craigieburn"],
  "mernda": ["south-morang", "epping-vic", "wollert"],
  "south-morang": ["mernda", "epping-vic", "wollert"],
  "tarneit": ["werribee", "melbourne-metro", "regional-victoria"],
  "clyde": ["melbourne-metro", "regional-victoria", "tarneit"],
  "werribee": ["tarneit", "melbourne-metro", "regional-victoria"],
  "melbourne-metro": ["wollert", "tarneit", "clyde"],
  "regional-victoria": ["melbourne-metro", "clyde", "werribee"],
};
